import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  User,
  Building2,
  Mail,
  Globe,
  CheckCircle2,
  Search,
  AlertCircle,
  Sparkles
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { useSite } from '@/contexts/SiteContext';
import { useAuth } from '@/context/auth-context';

interface Expert {
  id: string;
  name: string;
  role?: string;
  company?: string;
  email?: string;
  bio?: string;
  website_url?: string;
  expertise?: string[];
  photo_url?: string;
  expert_type?: 'product_expert' | 'founder' | 'executive' | 'self_demo';
  is_demo?: boolean;
}

interface ExpertSelectorProps {
  selectedExpertId?: string | null;
  onSelect: (expert: Expert, isDemoMode?: boolean) => void;
  showDemoOption?: boolean;
}

// Special ID for demo mode expert
export const DEMO_EXPERT_ID = 'self-demo-mode';

export function ExpertSelector({ selectedExpertId, onSelect, showDemoOption = true }: ExpertSelectorProps) {
  const { selectedSite } = useSite();
  const { user } = useAuth();
  const [experts, setExperts] = useState<Expert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Create demo expert from current user
  const demoExpert: Expert = {
    id: DEMO_EXPERT_ID,
    name: user?.name || user?.email?.split('@')[0] || 'You',
    email: user?.email || '',
    role: 'Product Expert (Demo)',
    company: selectedSite?.name || '',
    bio: 'Try the interview experience yourself before inviting real experts.',
    expert_type: 'self_demo',
    is_demo: true,
  };

  const handleSelectExpert = (expert: Expert) => {
    const isDemoMode = expert.id === DEMO_EXPERT_ID;
    onSelect(expert, isDemoMode);
  };

  useEffect(() => {
    loadExperts();
  }, [selectedSite]);

  const loadExperts = async () => {
    if (!selectedSite?.id) {
      setError('No site selected');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await authFetch(`/api/experts?siteId=${selectedSite.id}`);

      if (!response.ok) {
        throw new Error('Failed to load experts');
      }

      const data = await response.json();
      setExperts(data.experts || []);
    } catch (err) {
      console.error('[ExpertSelector] Error loading experts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load experts');
    } finally {
      setLoading(false);
    }
  };

  const filteredExperts = experts.filter(expert =>
    expert.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    expert.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    expert.role?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-2"></div>
          <p className="text-sm text-muted-foreground">Loading experts...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  // Show demo option even if no experts exist
  const hasExperts = experts.length > 0;

  return (
    <div className="space-y-4">
      {/* Demo Mode Card - Always shown first when enabled */}
      {showDemoOption && (
        <Card
          className={`cursor-pointer transition-all hover:shadow-md border-dashed border-2 ${
            selectedExpertId === DEMO_EXPERT_ID
              ? 'ring-2 ring-amber-500 border-amber-500 bg-amber-50 dark:bg-amber-950/20'
              : 'border-amber-300 hover:border-amber-500 bg-amber-50/50 dark:bg-amber-950/10'
          }`}
          onClick={() => handleSelectExpert(demoExpert)}
        >
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                  <Sparkles className="h-6 w-6 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-base">{demoExpert.name}</CardTitle>
                    <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-xs">
                      Demo Mode
                    </Badge>
                  </div>
                  <CardDescription className="mt-1">
                    Try the interview yourself first
                  </CardDescription>
                </div>
              </div>

              {selectedExpertId === DEMO_EXPERT_ID && (
                <CheckCircle2 className="h-5 w-5 text-amber-500 flex-shrink-0" />
              )}
            </div>
          </CardHeader>

          <CardContent className="pt-0">
            <p className="text-sm text-muted-foreground">
              Experience the interview flow before inviting real experts. You'll play the role of a product expert.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Divider */}
      {showDemoOption && hasExperts && (
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              Or select an existing expert
            </span>
          </div>
        </div>
      )}

      {/* Search - only show if there are experts */}
      {hasExperts && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search experts by name, company, or role..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      )}

      {/* No experts message */}
      {!hasExperts && !showDemoOption && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            No experts found for this site. Please create an expert first in the Experts Management page.
          </AlertDescription>
        </Alert>
      )}

      {/* Expert Cards */}
      {hasExperts && (
        <div className="grid gap-4 max-h-[400px] overflow-y-auto pr-2">
          {filteredExperts.map((expert) => (
            <Card
              key={expert.id}
              className={`cursor-pointer transition-all hover:shadow-md ${
                selectedExpertId === expert.id
                  ? 'ring-2 ring-primary border-primary bg-primary/5'
                  : 'hover:border-primary/50'
              }`}
              onClick={() => handleSelectExpert(expert)}
            >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {expert.photo_url ? (
                    <img
                      src={expert.photo_url}
                      alt={expert.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-6 w-6 text-primary" />
                    </div>
                  )}
                  <div>
                    <CardTitle className="text-base">{expert.name}</CardTitle>
                    {expert.role && (
                      <CardDescription className="flex items-center gap-1 mt-1">
                        <Building2 className="h-3 w-3" />
                        {expert.role}
                        {expert.company && ` @ ${expert.company}`}
                      </CardDescription>
                    )}
                  </div>
                </div>

                {selectedExpertId === expert.id && (
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                )}
              </div>
            </CardHeader>

            <CardContent className="pt-0">
              {expert.bio && (
                <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                  {expert.bio}
                </p>
              )}

              <div className="flex flex-wrap gap-2 mt-2">
                {expert.email && (
                  <Badge variant="outline" className="text-xs">
                    <Mail className="h-3 w-3 mr-1" />
                    {expert.email}
                  </Badge>
                )}
                {expert.website_url && (
                  <Badge variant="outline" className="text-xs">
                    <Globe className="h-3 w-3 mr-1" />
                    Website
                  </Badge>
                )}
                {expert.expertise && expert.expertise.slice(0, 3).map((skill, idx) => (
                  <Badge key={idx} variant="secondary" className="text-xs">
                    {skill}
                  </Badge>
                ))}
                {expert.expertise && expert.expertise.length > 3 && (
                  <Badge variant="secondary" className="text-xs">
                    +{expert.expertise.length - 3} more
                  </Badge>
                )}
              </div>
            </CardContent>
            </Card>
          ))}
        </div>
      )}

      {hasExperts && filteredExperts.length === 0 && searchTerm && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            No experts found matching "{searchTerm}"
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
